import axios from 'axios';
import { delay, get } from 'lodash';
import { SLASH } from '../data-types/constants';
import {
  DeleteInboxMessageOptions,
  DeleteInboxMessageResponse,
  InboxBadge,
  InboxData,
  InboxMessage,
  InboxRequestOptions,
} from '../data-types/inbox/inboxInterfaces';
import { DialogType } from '../data-types/notification/notificationEnums';
import { fontSizeRegex } from '../data-types/regex/regexConstants';
import { DEFAULT_RETRY_INTERVAL } from '../data-types/timing/timingConstants';
import { XTREMEPUSH_API_KEY } from '../initialisation/initialisation';
import { getSearchPathWithTrailingSlash } from '../routing-system/appPaths';
import browserHistory from '../routing-system/browserHistory';
import { enqueueDialog, setIsInboxOpen } from '../state/actions/dialogActions';
import { setInboxBadge } from '../state/actions/userDataActions';
import { store } from '../state/store';

export const NUMBER_OF_MESSAGES_PER_REQUEST = 10;

const emptyErrorCallback = () => undefined;
const emptySuccessCallback = () => undefined;

export const checkShowPushPermissionDialog = () => {
  const xtremepush = get(window, 'xtremepush');
  if (xtremepush) {
    xtremepush('ready', () => {
      const {
        userData: { user },
      } = store.getState();
      const isPushAllowedInUserPreferences = user && user.marketing && user.marketing.push;
      const permission = xtremepush('push', 'permission');
      if (permission === 'default' && isPushAllowedInUserPreferences) {
        enqueueDialog({ type: DialogType.PUSH_PERMISSION_DIALOG, content: {} });
      }
    });
  }
};

export const triggerPushPermissionPrompt = () => {
  const xtremepush = get(window, 'xtremepush');
  if (xtremepush) {
    const permission = xtremepush('push', 'permission');
    if (permission === 'default') {
      xtremepush('push', 'prompt');
    }
  }
};

export const isInboxFeatureEnabled = () => Boolean(JSON.parse(process.env.REACT_APP_INBOX_FEATURE_ENABLED || 'false'));

export const loadInboxBadge = () => {
  const xtremepush = get(window, 'xtremepush');
  if (xtremepush) {
    xtremepush('ready', () => {
      const options = {};
      const successCallback = (badgeResponse: InboxBadge) => setInboxBadge(badgeResponse.badge);
      xtremepush('inbox', 'badge', options, successCallback, emptyErrorCallback);
    });
  }
};

const retryToLoadInboxBadge = () => {
  const {
    userData: { inboxBadge },
  } = store.getState();

  if (inboxBadge < 0) {
    loadInboxBadge();
  }
};

export const attemptToLoadInboxBadge = () => {
  if (isInboxFeatureEnabled()) {
    loadInboxBadge();
    delay(retryToLoadInboxBadge, 3 * DEFAULT_RETRY_INTERVAL);
  }
};

export const openInbox = () => {
  setIsInboxOpen(true);
  loadInboxBadge();
};

export const setXtremepushUserId = (userId: number | undefined) => {
  const xtremepush = get(window, 'xtremepush');
  if (xtremepush) {
    xtremepush('ready', () => {
      if (userId) {
        xtremepush('set', 'user_id', `${userId}`);
      } else {
        xtremepush('set', 'user_id', '');
      }
    });
  }
};

const sanitizeInboxMessages = (inboxMessages: InboxMessage[]) =>
  inboxMessages
    .filter(
      inboxMessage =>
        inboxMessage.id &&
        typeof inboxMessage.create_time === 'number' &&
        inboxMessage.message &&
        typeof inboxMessage.message === 'object' &&
        inboxMessage.message.cid,
    )
    .map(inboxMessage => ({
      ...inboxMessage,
      message: {
        ...inboxMessage.message,
        title: inboxMessage.message.title || '',
        alert: inboxMessage.message.alert || '',
        style: inboxMessage.message.style || {},
      },
    }));

const sanitizeInboxData = (inboxData: InboxData) => {
  const sanitizedData: InboxData = {
    items: inboxData.items && Array.isArray(inboxData.items) ? sanitizeInboxMessages(inboxData.items) : [],
    more: typeof inboxData.more === 'boolean' ? inboxData.more : false,
    badge: typeof inboxData.badge === 'number' ? inboxData.badge : 0,
  };

  return sanitizedData;
};

export const loadInboxData = (onSuccess: (inboxData: InboxData) => void, onError: () => void, options: InboxRequestOptions) => {
  const xtremepush = get(window, 'xtremepush');

  if (xtremepush) {
    xtremepush('ready', () => {
      const successCallback = (inboxData: InboxData) => onSuccess(sanitizeInboxData(inboxData));

      xtremepush('inbox', 'message.list', options, successCallback, onError);
    });
  }
};

export const reportLinkInMessageClicked = (messageId: number) => {
  const xtremepush = get(window, 'xtremepush');
  if (xtremepush) {
    xtremepush('ready', () => {
      const actionOptions = {
        id: messageId,
        click: 1,
      };

      xtremepush('inbox', 'message.action', actionOptions, emptySuccessCallback, emptyErrorCallback);
    });
  }
};

export const markInboxMessageOpened = (messageId: number, onSuccess: () => void) => {
  const xtremepush = get(window, 'xtremepush');
  if (xtremepush) {
    xtremepush('ready', () => {
      const actionOptions = {
        id: messageId,
        open: 1,
      };

      const successCallback = (response: InboxBadge) => {
        setInboxBadge(response.badge);
        onSuccess();
      };

      xtremepush('inbox', 'message.action', actionOptions, successCallback, emptyErrorCallback);
    });
  }
};

export const processInboxMessageTitleForList = (stringifiedHtmlTitle: string) => stringifiedHtmlTitle.replace(fontSizeRegex, '');

export const deleteInboxMessage = async (cid: string, onSuccess: () => void, onError: () => void) => {
  const {
    userData: { user },
  } = store.getState();
  const campaignId = Number(cid);
  const url = 'https://external-api.xtremepush.com/api/external/remove/inbox-message';

  if (XTREMEPUSH_API_KEY && user && user.id && campaignId) {
    const data: DeleteInboxMessageOptions = {
      apptoken: XTREMEPUSH_API_KEY,
      campaign_id: campaignId,
      user_id: user.id,
    };

    try {
      const response = await axios.post(url, JSON.stringify(data), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const res = response.data as DeleteInboxMessageResponse;

      if (res.code === 200) {
        onSuccess();
      } else {
        onError();
      }
    } catch {
      onError();
    }
  }
};

export const handleInboxMessageDeeplinkClick = (deeplink: string, openInAppCallback: () => void) => {
  if (deeplink.startsWith(SLASH)) {
    const adjustedDeeplink = getSearchPathWithTrailingSlash(deeplink);

    browserHistory.push(adjustedDeeplink);
    openInAppCallback();
  } else {
    window.location.replace(deeplink);
  }
};
